import type { BaseEntityState, AuditFields } from '@crm/shared';
import type { ExtractorGroup, MessageInfo } from './messageInfoTypes';

/**
 * Stato di lavorazione del messaggio server-driven.
 * Mappa 1:1 l'enum backend MessageLabel (it.dvel.crm.util.constants.MessageLabel):
 *   0 = NEW, 1 = TO_PROCESS, 2 = IN_PROGRESS, 3 = COMPLETED, 4 = IGNORED, 10 = ERROR.
 * Conversione int <-> union string in messageMappers.
 */
export type MessageLabel = 'new' | 'to_process' | 'in_progress' | 'completed' | 'ignored' | 'error';

/**
 * Direzione del messaggio rispetto alla casella. Mappa 1:1 l'enum backend
 * MessageDirection: IN = ricevuto in ingresso, OUT = inviato in uscita.
 */
export type MessageDirection = 'IN' | 'OUT';

/**
 * Descrittore UI di ciascun valore di MessageLabel: chiave i18n per il
 * testo tradotto e color token MUI per il Chip di stato.
 */
export const MESSAGE_LABELS: {
  value: MessageLabel;
  label: string;
  color: 'default' | 'warning' | 'info' | 'success' | 'error';
}[] = [
  { value: 'new', label: 'message.labels.new', color: 'default' },
  { value: 'to_process', label: 'message.labels.to_process', color: 'warning' },
  { value: 'in_progress', label: 'message.labels.in_progress', color: 'info' },
  { value: 'completed', label: 'message.labels.completed', color: 'success' },
  { value: 'ignored', label: 'message.labels.ignored', color: 'error' },
  { value: 'error', label: 'message.labels.error', color: 'error' },
];

export interface Message extends AuditFields {
  messageId: number;
  /** Direzione rispetto alla casella: IN (ricevuto) / OUT (inviato). Obbligatoria sul dato reale. */
  direction: MessageDirection;
  /**
   * Campi di import/threading valorizzati dal backend (sola lettura lato UI). Opzionali nel modello
   * UI: i mock non li popolano e non tutte le viste li usano.
   */
  externalId?: string;
  parentExternalId?: string;
  rootExternalId?: string;
  subject: string;
  /** Corpo in testo semplice: ricerca, anteprima lista, snippet. */
  body: string;
  /**
   * Corpo HTML grezzo (non sanitizzato) per il display ricco. Opzionale: vuoto/assente se la
   * mail non ha una parte HTML. Va renderizzato in modo isolato (iframe sandboxed), mai
   * inserito direttamente nel DOM.
   */
  bodyHtml?: string;
  senderName: string;
  senderEmailAddress: string;
  receiptDate: string;
  label: MessageLabel;
  /** Riassunto del messaggio generato lato server; opzionale lato UI. */
  summary?: string;
  /**
   * Info polimorfiche associate al messaggio, eager-load dal backend
   * (ordinate per priority asc). Modificabili solo via endpoint dedicato
   * PATCH /message/{mid}/info/{iid} — mai incluse nei payload di
   * create/update del Message stesso.
   */
  infos: MessageInfo[];
  /**
   * Info prodotte dagli extractor abilitati per l'utente/casella (label da
   * MessageExtractor.getKeys lato backend), eager-load e distinte dalle info
   * predefinite di `infos`. Dopo TS-10 arrivano gia' raggruppate per extractor
   * di provenienza (ExtractorGroup), ordinate per priority dell'extractor.
   * Opzionale lato UI: non tutte le viste la usano e i mock non sempre la
   * popolano. A differenza di `infos` (read-only), le info dentro i gruppi sono
   * editabili via PATCH /message/{mid}/info/{iid}.
   */
  extractorInfos?: ExtractorGroup[];
  /**
   * Allegati del messaggio (solo metadati), eager-load dal backend e ordinati per nome file.
   * Opzionale lato UI: i mock non li popolano e non tutte le viste li usano. Il contenuto del
   * file risiede su Document Library; il download sarà esposto separatamente.
   */
  attachments?: MessageAttachment[];
}

/**
 * Metadati di un allegato di un messaggio (il contenuto risiede su Document Library lato backend).
 * Read-only lato UI.
 */
export interface MessageAttachment {
  messageAttachmentId: number;
  messageId: number;
  fileName: string;
  extension: string;
  mimeType: string;
  /** Dimensione in byte. */
  size: number;
}

export interface AiAnalysis {
  suggestedNextStep: string;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

/**
 * Proiezione di un Message per la timeline attività (sezione Email): i campi mostrati
 * dall'accordion (chiuso + aperto), corpo incluso — l'accordion aperto rende il corpo HTML della
 * mail. NON è un Message completo (niente infos, allegati, analisi AI), quindi vive in una cache
 * dedicata (`state.messages.summariesById`), separata da `byId` (usata dalla smart inbox con il
 * Message pieno). Endpoint `GET /message/{id}/summary`; nessuna conversione, mappa 1:1 il DTO.
 * Evita il costo di `GET /message/{id}` (join eager) e viene caricata LAZY all'espansione
 * dell'accordion, mai in bulk: i corpi delle mail non aperte non si scaricano.
 */
export interface MessageSummary {
  messageId: number;
  subject: string;
  /** Corpo in testo semplice: fallback dell'accordion quando la mail non ha una parte HTML. */
  body: string;
  /**
   * Corpo HTML grezzo (NON sanitizzato); assente se la mail non ha una parte HTML. Come per
   * {@link Message.bodyHtml}: va reso in un iframe sandboxed, mai inserito nel DOM.
   */
  bodyHtml?: string;
  direction: MessageDirection;
  receiptDate: string;
  senderName: string;
  senderEmailAddress: string;
  /**
   * Metadati degli allegati (TS-108). Attenzione: NON arrivano da
   * `GET /message/{id}/summary`, che resta la sola proiezione del corpo — li porta la proiezione
   * bulk della timeline (ActivitySummary), quindi sono valorizzati sulla `MessageSummary`
   * ricostruita da lì e vuoti su quella fetchata. Chi li mostra legge la prima.
   */
  attachments?: MessageAttachment[];
}

export interface MessageState extends BaseEntityState<Message> {
  selectedMessageId: number | null;
  aiAnalysis: AiAnalysis | null;
  /** Totale records lato server per pagination. Tabs filtrano `items` client-side per label. */
  totalCount: number;
  /** Cache delle proiezioni leggere (timeline attività), separata da `byId` (Message pieno). */
  summariesById: Record<number, MessageSummary>;
}
